import {
  Business as CompanyIcon,
  Person as PersonIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import React from "react";
import { MyCustomerApiRequest } from "../../../store/services/customerService";
import { useGetCustomerGroupQuery } from "../../../store/services/customerService";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

export type CustomerTypeFilter = "company" | "mark" | "private";

interface CustomerTypeFilterTabsProps {
  activeFilters: MyCustomerApiRequest;
  onFilterChange: (filter: MyCustomerApiRequest) => void;
}

const CustomerTypeFilterTabs: React.FC<CustomerTypeFilterTabsProps> = ({
  activeFilters,
  onFilterChange,
}) => {
  const selectedUserId = useSelector(
    (state: RootState) => state.user.selectedUserId
  );
  const { data: customerGroups } = useGetCustomerGroupQuery({
    userNo: selectedUserId ?? undefined,
    customerType: activeFilters.customerType,
  });

  const tabs = [
    {
      key: "company" as const,
      label: "Företag",
      icon: <CompanyIcon />,
    },
    {
      key: "mark" as const,
      label: "Märkning/Grupp",
      icon: <PersonIcon />,
      infoText: "Här kan du endast filtrera grupper/märkningar för företag som du är säljare för. Ingår privatpersoner i samma grupp får du gå via företaget och vidare till vyn för privatpersoner."
    },
    {
      key: "private" as const,
      label: "Privatpersoner",
      icon: <PersonIcon />,
      infoText: "Som default visas privatpersoner utan grupp/märkning. Välj \"Märkning/Grupp\" för att se privatpersoner som är kopplade till en grupp/märkning."
    },
  ];

  const selectedGroup =
    customerGroups?.find(
      (group) => group.Id === activeFilters.customerGroupNo
    ) || null;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={2}>
        <Stack
          direction="row"
          spacing={0}
          sx={{
            backgroundColor: "background.paper",
            borderRadius: 1.5,
            p: 0.25,
            border: 1,
            borderColor: "divider",
            width: "fit-content",
          }}
        >
          {tabs.map((tab) => (
            <Box key={tab.key} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant={
                  activeFilters.customerType === tab.key ? "contained" : "text"
                }
                color="primary"
                startIcon={tab.icon}
                onClick={() =>
                  onFilterChange({ ...activeFilters, customerType: tab.key })
                }
                size="small"
                sx={{
                  borderRadius: 1,
                  px: 2,
                  py: 0.75,
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  minWidth: 100,
                  boxShadow: activeFilters.customerType === tab.key ? 1 : 0,
                  "& .MuiButton-startIcon": {
                    marginRight: 0.75,
                    "& .MuiSvgIcon-root": {
                      fontSize: "1rem",
                    },
                  },
                  "&:hover": {
                    backgroundColor:
                      activeFilters.customerType === tab.key
                        ? "primary.main"
                        : "action.hover",
                  },
                }}
              >
                {tab.label}
              </Button>
              {tab.infoText && (
                <Tooltip 
                  title={tab.infoText}
                  arrow
                  placement="top"
                  enterDelay={300}
                  leaveDelay={200}
                >
                  <InfoIcon
                    sx={{
                      ml: 0.5,
                      fontSize: "1rem",
                      color: "text.secondary",
                      cursor: "help",
                      "&:hover": {
                        color: "primary.main",
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          ))}
        </Stack>
        <Stack direction="row" spacing={2}>
          {activeFilters.customerType !== "company" && (
            <Autocomplete
              value={selectedGroup}
              onChange={(_, newValue) => {
                onFilterChange({
                  ...activeFilters,
                  customerGroupNo: newValue?.Id,
                });
              }}
              options={customerGroups || []}
              getOptionLabel={(option) => option.Name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Välj grupp/märkning"
                  size="small"
                  sx={{
                    minWidth: 250,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "background.paper",
                    },
                  }}
                />
              )}
              clearOnBlur
              handleHomeEndKeys
              sx={{
                "& .MuiAutocomplete-clearIndicator": {
                  visibility: selectedGroup ? "visible" : "hidden",
                },
              }}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default CustomerTypeFilterTabs;
